import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Eye, EyeOff, CheckCircle, AlertCircle } from 'lucide-react';
import clientPortalService from '../../services/clientPortalService';

export default function PortalPinSetup() {
  const navigate = useNavigate();
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [showPin, setShowPin] = useState(true);
  const [showConfirmPin, setShowConfirmPin] = useState(true);
  const [acknowledged, setAcknowledged] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [pinLength, setPinLength] = useState(6); // canonical default; overridden by /pin-status if available

  useEffect(() => {
    let active = true;
    clientPortalService.getPinStatus()
      .then((res) => {
        const len = res?.pin_length ?? res?.data?.pin_length;
        if (active && Number.isInteger(len) && len > 0) setPinLength(len);
      })
      .catch(() => { /* keep default 6 on failure or missing field */ });
    return () => { active = false; };
  }, []);

  const matchState = useMemo(() => {
    if (confirmPin.length === 0) return null;
    return pin === confirmPin ? 'match' : 'mismatch';
  }, [pin, confirmPin]);

  const pinValid = pin.length === pinLength;
  const pinsMatch = matchState === 'match';
  const canSubmit = pinValid && pinsMatch && acknowledged && !saving;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!canSubmit) return;
    setSaving(true);
    try {
      await clientPortalService.setPin(pin);
      clientPortalService.clearPinSetupFlag();
      setSuccess(true);
      setTimeout(() => {
        if (clientPortalService.needsIntake()) {
          navigate('/portal/intake');
        } else {
          navigate('/portal/dashboard');
        }
      }, 1500);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to set PIN');
      setSaving(false);
    }
  };

  const eyeButton = (visible, toggle) => (
    <button
      type="button"
      onClick={toggle}
      className="absolute right-3 top-1/2 -translate-y-1/2 text-virgo-teal-400 hover:text-white"
      aria-label={visible ? 'Hide PIN' : 'Show PIN'}
      tabIndex={-1}
    >
      {visible ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
    </button>
  );

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-virgo-navy-900 via-virgo-navy-800 to-virgo-navy-900 flex items-center justify-center px-4">
        <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-green-500/30 p-8 max-w-md w-full text-center">
          <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">PIN Created!</h2>
          <p className="text-virgo-teal-300">Your security PIN has been set. Redirecting...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-virgo-navy-900 via-virgo-navy-800 to-virgo-navy-900 py-12 px-4">
      <div className="max-w-md mx-auto">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 mb-4">
            <Shield className="w-8 h-8 text-white" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <h1 className="text-2xl font-bold text-white mb-2">
                Set your phone verification PIN
              </h1>
              <p className="text-virgo-teal-300 text-sm leading-relaxed">
                Your law firm will ask for this PIN when you call the office or
                if they need to verify your identity over the phone. Choose {pinLength}
                digits you'll remember — but write them down somewhere safe in
                case you forget.
              </p>
            </div>

            {error && (
              <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-3 flex items-center gap-2 text-red-300 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-virgo-teal-300 mb-2">
                {pinLength}-digit PIN
              </label>
              <div className="relative">
                <input
                  type={showPin ? 'text' : 'password'}
                  value={pin}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, pinLength);
                    setPin(val);
                  }}
                  maxLength={pinLength}
                  inputMode="numeric"
                  pattern={`[0-9]{${pinLength}}`}
                  autoComplete="off"
                  className="w-full px-4 py-3 bg-virgo-navy-800 border border-virgo-teal-700 rounded-lg text-white text-center text-2xl tracking-widest focus:ring-2 focus:ring-virgo-teal-500 focus:border-transparent pr-12"
                  autoFocus
                />
                {eyeButton(showPin, () => setShowPin(!showPin))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-virgo-teal-300 mb-2">
                Confirm PIN
              </label>
              <div className="relative">
                <input
                  type={showConfirmPin ? 'text' : 'password'}
                  value={confirmPin}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, pinLength);
                    setConfirmPin(val);
                  }}
                  maxLength={pinLength}
                  inputMode="numeric"
                  pattern={`[0-9]{${pinLength}}`}
                  autoComplete="off"
                  className="w-full px-4 py-3 bg-virgo-navy-800 border border-virgo-teal-700 rounded-lg text-white text-center text-2xl tracking-widest focus:ring-2 focus:ring-virgo-teal-500 focus:border-transparent pr-12"
                />
                {eyeButton(showConfirmPin, () => setShowConfirmPin(!showConfirmPin))}
              </div>
              {matchState && (
                <p
                  className={`mt-2 text-sm ${
                    matchState === 'match' ? 'text-green-300' : 'text-red-300'
                  }`}
                >
                  {matchState === 'match' ? '✓ Matches' : "✗ Doesn't match"}
                </p>
              )}
            </div>

            <p className="text-xs text-virgo-teal-400/70">
              Tip: avoid your birthday, address, or 123456. Pick something
              memorable but not obvious.
            </p>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
                className="mt-1 w-4 h-4 rounded border-virgo-teal-700 bg-virgo-navy-800 text-cyan-500 focus:ring-cyan-500 focus:ring-offset-0 cursor-pointer"
              />
              <span className="text-sm text-virgo-teal-300 select-none">
                I've written down or remembered this PIN
              </span>
            </label>

            <button
              type="submit"
              disabled={!canSubmit}
              className="w-full py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-semibold rounded-lg hover:from-cyan-600 hover:to-blue-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : 'Save and Continue'}
            </button>
          </form>

          <p className="text-virgo-teal-500 text-xs text-center mt-6">
            Your PIN is encrypted and stored securely. It is never visible to
            staff.
          </p>
        </div>
      </div>
    </div>
  );
}
